import React, { useEffect, useState } from "react";
import { useReportAutoRun } from "hooks/useReportAutoRun/useReportAutoRun";
import { useLocation } from "react-router-dom";

import { downloadCsv, formatDate, openPrintWindow } from "helpers/Helpers";

import { GetReport } from "api/rpc/2024-04/facilityAdmin/report/report";
import { StatusCode } from "api/protocols";
import { IConfiguration } from "api/rpc/clientAdmin/client/report";

import Page from "components/page/Page";
import Card from "components/card/Card";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import AccountsReceivableTable from "../tables/AccountsReceivableTable";
import Checkbox from "components/form/checkbox/Checkbox";
import { useTranslation } from "react-i18next";

import "../report.scss";
import { useAppDispatch } from "hooks/redux";
import { showError, showSuccess } from "redux/actions/ui";
import ReportEmailModal from "components/reportEmailModal/ReportEmailModal";
import moment from "moment";
import { useReportRun } from "../useReportRun";

interface IReportState {
  id: number;
  title: string;
  code: string;
  dateRangeStart: Date;
  dateRangeEnd: Date;
  singleDate: Date;
  configurations: Array<IConfiguration>;
  date_range: Array<string>;
  date: string;
  facilityOptions: Array<{ label: string; onClick: () => void }>;
  facility_id_name: string;
  email: string;
  queue_after_days: number;
  [key: string]: any;
}

interface IReportDataState {
  headings: any;
  rows: any;
  columns: Array<Record<string, any>>;
  tableDisplay: boolean;
  description: string;
  compileDate: string;
}

export default function AccountsReceivable() {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const GetReportRun = useReportRun();
  const routeName = location.pathname.split("/");
  const code = routeName[routeName.length - 1];
  const { t } = useTranslation();

  const today = new Date();

  const [state, setState] = useState<IReportState>({
    dateRangeStart: today,
    dateRangeEnd: today,
    singleDate: today,
    id: null,
    title: "",
    code: "",
    configurations: [],
    date_range: [],
    date: formatDate(today),
    facilityOptions: [],
    facility_id_name: "",
    zero_balance: false,
    includeClosedAccounts: false,
    email: "",
    queue_after_days: 14,
  });

  const [reportData, setReportData] = useState<IReportDataState>({
    headings: [],
    rows: [],
    columns: [],
    tableDisplay: false,
    description: "",
    compileDate: "",
  });

  useEffect(() => {
    void loadReport();
  }, []);
  useReportAutoRun({
    isReady: state.id != null,
    reportId: state.id,
    setState,
    run: () => {
      void getReport(state.email);
    },
  });


  async function loadReport() {
    const reportRes = await GetReport({ code: code }, true);
    if (reportRes.status !== StatusCode.OK) {
      return;
    }

    const report = reportRes.data[0];
    const sortedConfigurations = [...report.configurations].sort((prev, next) => prev.position - next.position);

    setState(prev => ({
      ...prev,
      id: report.id,
      title: report.title,
      code: report.code,
      queue_after_days: report.queue_after_days,
      configurations: sortedConfigurations,
    }));
  }

  async function getReport(email: string) {
    const reportRes = await GetReportRun(
      {
        report_id: state.id,
        date: state.date,
        zero_balance: state.zero_balance,
        include_closed_accounts: state.includeClosedAccounts,
        email: email,
      },
      true,
    );

    if (reportRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.reports.pages.accounts_receivable.008")));
      return;
    }

    if (reportRes.message) {
      dispatch(showSuccess(reportRes.message));
    }

    const compileDate = reportRes.data?.compiled_at ? reportRes.data?.compiled_at.concat("Z") : "";

    setReportData(prev => ({
      ...prev,
      rows: reportRes.data.rows,
      columns: reportRes.data.columns,
      description: reportRes.data?.description ?? "",
      compileDate,
    }));

    return;
  }

  async function getDownload(email: string) {
    const segmentsRes = await GetReportRun(
      {
        report_id: state.id,
        type: "csv",
        date: state.date,
        zero_balance: state.zero_balance,
        include_closed_accounts: state.includeClosedAccounts,
        email: email,
      },
      true,
    );

    if (segmentsRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.reports.pages.accounts_receivable.008")));
      return;
    }

    if (!email) {
      downloadCsv(segmentsRes.data as string, reportFilename() + '.csv');
    } else {
      dispatch(showSuccess(segmentsRes.message));
    }
  }

  async function getPrint(email: string) {
    const printRes = await GetReportRun(
      {
        report_id: state.id,
        type: "print",
        date: state.date,
        zero_balance: state.zero_balance,
        include_closed_accounts: state.includeClosedAccounts,
        email: email,
      },
      true,
    );

    if (printRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.reports.pages.accounts_receivable.009")));
      return;
    }

    if (!email) {
      openPrintWindow(printRes.data as string);
    } else {
      dispatch(showSuccess(printRes.message));
    }
  }

  function setSingleDate(date: Date) {
    setState(prevState => ({
      ...prevState,
      singleDate: date,
      date: formatDate(date),
    }));
  }

  const [reportEmail, setReportEmail] = useState({
    handler: null,
    open: false,
  });

  function handleReportEmailSelected(email: string) {
    setState({ ...state, email });
    setReportEmail({
      ...reportEmail,
      open: false,
    });
  }

  function handleReportEmailClose() {
    setReportEmail({ handler: null, open: false });
  }

  useEffect(() => {
    if (reportEmail.open === false && reportEmail.handler !== null) {
      reportEmail.handler(state.email);
    }
  }, [reportEmail.open]);

  const primaryAction = {
    content: t("secure.facility.reports.pages.accounts_receivable.001"),
    action: () => {
      if (reportShouldQueue()) {
        dispatch(showSuccess(
          t('Potential large report detected.'), 
          10000,
          t('An email is required to send the report to upon completion.'),
        ));

        setReportEmail({
          open: true,
          handler: getReport,
        });
      } else {
        void getReport(state.email);
      }
    },
  };

  function reportFilename(): string {
    return `${state.title}_${moment(new Date()).format("YYYY-MM-DD").toString()}`
        .replace(/ /g, "_")
        .toLowerCase();
  }

  function reportShouldQueue(): boolean {
    return moment(state.dateRangeEnd).diff(moment(state.dateRangeStart), 'days') >= state.queue_after_days;
  }

  function handleDownload() {
    if (reportShouldQueue()) {
      setReportEmail({
        open: true,
        handler: getDownload,
      });
    } else {
      void getDownload('');
    }
  }

  function handlePrint() {
    if (reportShouldQueue()) {
      setReportEmail({
        open: true,
        handler: getPrint,
      });
    } else {
      void getPrint('');
    }
  }

  return (
    <Page
      title={t("secure.facility.reports.pages.accounts_receivable.002")}
      primaryAction={primaryAction}
      multipleActionDropdownAction={{
        label: t("secure.facility.reports.pages.accounts_receivable.003"),
        dropdownProps: {
          alignment: "right",
          options: [
            {
              type: "handler",
              label: t("secure.facility.reports.pages.accounts_receivable.004"),
              handler: handleDownload,
              icon: "file-arrow-down",
            },
            {
              type: "handler",
              label: t("secure.facility.reports.pages.accounts_receivable.005"),
              handler: handlePrint,
              icon: "print",
            },
          ],
        },
      }}
      breadcrumbs={[
        { prefix: true, label: t("secure.facility.reports.pages.accounts_receivable.006"), url: "/admin/report" },
      ]}
    >
      <Card>
        <Card.Section>
          <div className="report-filters-container">
            <DatePickerInput
              months={1}
              position="left"
              startingDate={state.singleDate}
              setStartingDate={setSingleDate}
            />
            <Checkbox
              label={t("secure.facility.reports.pages.accounts_receivable.007")}
              checked={state.zero_balance}
              onChange={e => setState(prevState => ({ ...prevState, zero_balance: e.target.checked }))}
              size="medium"
            />
            <Checkbox
              id="includeClosedAccounts"
              label={t("Include Closed Accounts")}
              checked={state.includeClosedAccounts}
              value={state.includeClosedAccounts}
              onChange={e => setState(prevState => ({ ...prevState, includeClosedAccounts: e.target.checked }))}
              size="medium"
            />
          </div>
        </Card.Section>
      </Card>

      <div className="dynamic-table-container">
        <AccountsReceivableTable
          data={reportData.rows}
          columns={reportData.columns}
          description={reportData.description}
          compileDate={reportData.compileDate}
        />
      </div>
      <ReportEmailModal
        open={reportEmail.open}
        onEmailSelected={handleReportEmailSelected}
        onClose={handleReportEmailClose}
      />
    </Page>
  );
}
